import { AppTagNav, AvatarDropdown, AvatarName } from "@/components";
import { AppRoleType } from "@/constants/role";
import auth from "@/services/auth";
import { AntdStaticInit } from "@/utils/antdStatic";
import type { Settings as LayoutSettings } from "@ant-design/pro-components";
import { SettingDrawer } from "@ant-design/pro-components";
import "@ant-design/v5-patch-for-react-19";
import type { RequestConfig, RunTimeLayoutConfig } from "@umijs/max";
import { history } from "@umijs/max";
import defaultSettings from "../config/defaultSettings";
import { errorConfig } from "./requestErrorConfig";

const isDev = process.env.NODE_ENV === "development";
const loginPath = "/user/login";
const feishuCallbackPath = "/user/login/feishu/callback";
const runtimeApiBaseUrl =
  typeof API_BASE_URL === "undefined" ? "" : API_BASE_URL;
const publicPaths = [
  loginPath,
  feishuCallbackPath,
  "/user/register",
  "/user/register-result",
];
const appHomePath = "/app";

const isPublicPath = (pathname: string) => publicPaths.includes(pathname);
const isSystemRole = (currentUser?: API.AppCurrentUser) =>
  currentUser?.roles?.some((role) => Number(role.type) === AppRoleType.System) ??
  false;

const buildLoginRedirectPath = (pathname: string, search = "") => {
  const searchParams = new URLSearchParams({
    redirect: `${pathname}${search}`,
  });
  return `${loginPath}?${searchParams.toString()}`;
};

// 已登录用户访问登录页时，按 redirect 参数跳回原目标。
// 收敛到此处统一处理，避免在 Login 组件里做渲染期跳转。
const redirectAuthedAwayFromLogin = (location: {
  pathname: string;
  search: string;
}) => {
  if (location.pathname !== loginPath) return false;
  if (!auth.session.getAuthToken()) return false;
  const target = auth.feishu.normalizeRedirectPath(
    new URLSearchParams(location.search).get("redirect"),
  );
  history.replace(target);
  return true;
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.AppCurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.AppCurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    if (!auth.session.getAuthToken()) {
      auth.session.clearCurrentUser();
      return undefined;
    }

    try {
      const currentUser = await auth.api.getCurrentUserInfo({
        skipErrorHandler: true,
      });
      auth.session.setCurrentUser(currentUser);
      return currentUser;
    } catch (_error) {
      auth.session.clearSession();
      const { pathname, search } = history.location;
      if (!isPublicPath(pathname)) {
        history.push(buildLoginRedirectPath(pathname, search));
      }
    }
    return undefined;
  };
  const { location } = history;
  // 已登录访问登录页：先跳回原目标，再补齐用户信息，避免登录页闪一下。
  const shouldFetchUserInfo =
    redirectAuthedAwayFromLogin(location) || !isPublicPath(location.pathname);
  const currentUser = shouldFetchUserInfo
    ? await fetchUserInfo()
    : undefined;
  return {
    fetchUserInfo,
    currentUser,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const admin = isSystemRole(initialState?.currentUser);
  return {
    actionsRender: () => [],
    avatarProps: {
      src: initialState?.currentUser?.avatarUrl,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: false,
    onPageChange: () => {
      const { location } = history;
      // 已登录访问登录页：跳回 redirect 指定的业务页面
      if (redirectAuthedAwayFromLogin(location)) return;
      // 未登录访问受保护页：跳到登录页并带上 redirect
      if (
        !initialState?.currentUser &&
        !auth.session.getAuthToken() &&
        !isPublicPath(location.pathname)
      ) {
        history.push(
          buildLoginRedirectPath(location.pathname, location.search)
        );
        return;
      }
      if (
        initialState?.currentUser &&
        !admin &&
        !isPublicPath(location.pathname) &&
        location.pathname !== appHomePath
      ) {
        history.replace(appHomePath);
      }
    },
    bgLayoutImgList: [
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr",
        left: 85,
        bottom: 100,
        height: "303px",
      },
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr",
        bottom: -68,
        right: -45,
        height: "303px",
      },
      {
        src: "https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr",
        bottom: 0,
        left: 0,
        width: "331px",
      },
    ],
    menu: {
      locale: false,
      params: {
        userId: initialState?.currentUser?.userId,
      },
      request: async (_params, defaultMenuData) => {
        if (!admin) {
          return [];
        }
        try {
          const menus = await auth.menu.getAuthMenus({
            skipErrorHandler: true,
          });
          if (!menus.length) {
            return defaultMenuData;
          }
          return auth.menu.transformBackendMenus(menus);
        } catch (_error) {
          return defaultMenuData;
        }
      },
    },
    menuRender: admin ? undefined : false,
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      const isAppPage = history.location.pathname === appHomePath;
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          <AntdStaticInit />
          {isAppPage ? (
            <div className="page-app-root">
              <AppTagNav />
              {children}
            </div>
          ) : children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: runtimeApiBaseUrl,
  withCredentials: true,
  ...errorConfig,
};
